/**
 * IndustryCtaBanner — the single highest-leverage cold-start prompt.
 *
 * Setting the firm's industry (NAICS) unlocks the entire day-1 intelligence
 * layer: industry benchmarks, progressive-blend priors, and benchmark-backed
 * forecasts — all of which work with ZERO tenant data. Yet it used to be a
 * buried, skippable settings field. This banner shows on every dashboard
 * view until it's set (dismissible per browser, returns next session).
 *
 * Also renders the "sample data" whisper when the workspace is seeded with
 * demo rows, so nobody mistakes sample signals for real ones.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';

const DISMISS_KEY = 'waypoint.industry-banner-dismissed-at';

function dismissedRecently(): boolean {
  try {
    const at = localStorage.getItem(DISMISS_KEY);
    if (!at) return false;
    // Re-surface after 24h — this unlocks too much value to dismiss forever.
    return Date.now() - Number(at) < 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function IndustryCtaBanner() {
  const [dismissed, setDismissed] = useState(dismissedRecently);

  const { data } = useQuery({
    queryKey: ['settings', 'industry-banner'],
    queryFn: () =>
      api<{ settings?: { firm_naics_code?: string | null; sample_data_seeded?: boolean } }>(
        '/v1/settings',
      ).then((r) => r.settings ?? {}),
    staleTime: 60_000,
  });

  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState(false);

  if (!data) return null;

  async function removeSample() {
    if (!window.confirm('Remove the sample deals, clients, meetings and contracts from your workspace? Your real data is not touched.')) return;
    setRemoving(true);
    try {
      await api('/v1/demo/sample', { method: 'DELETE' });
      await queryClient.invalidateQueries();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Could not remove sample data. Try again.');
    } finally {
      setRemoving(false);
    }
  }

  const sampleChip = data.sample_data_seeded ? (
    <div className="flex items-center gap-2 border-b border-violet-500/20 bg-violet-500/5 px-4 py-1">
      <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-2xs font-semibold text-violet-300">
        Sample data
      </span>
      <span className="text-2xs text-ink-dim">
        You're exploring with sample data — connect a real integration any time to replace it.
      </span>
      <button
        onClick={removeSample}
        disabled={removing}
        className="ml-auto text-2xs text-violet-300 hover:text-violet-200 underline disabled:opacity-50"
      >
        {removing ? 'Removing…' : 'Remove sample data'}
      </button>
    </div>
  ) : null;

  if (data.firm_naics_code || dismissed) return sampleChip;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 border-b border-primary/25 bg-primary/10 px-4 py-2">
        <span className="text-2xs text-ink">
          <span className="font-semibold">Unlock day-one intelligence:</span> set your industry
          and Waypoint immediately shows how you compare to peers — churn, win rates, payment
          speed — even before your data syncs.
        </span>
        <div className="flex shrink-0 items-center gap-3">
          <Link
            to="/dashboard/settings?tab=industry"
            className="rounded-lg bg-primary px-3 py-1 text-2xs font-semibold text-white hover:brightness-110"
          >
            Set industry
          </Link>
          <button
            onClick={() => {
              try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* private mode */ }
              setDismissed(true);
            }}
            className="text-2xs text-ink-dim hover:text-ink"
            aria-label="Dismiss"
          >
            Later
          </button>
        </div>
      </div>
      {sampleChip}
    </div>
  );
}
